<template>
  <div class="page-container me-page">
    <portal to="page-name">Meu perfil</portal>
    <div class="card">
      <div class="loading mt-2" v-if="loading" />
      <div class="card-header">
        <div class="card-title">
          Meu perfil
        </div>
      </div>
      <div class="card-body">
        <label class="form-label">Dados principais</label>
        <div class="me-group">
          <div class="columns">
            <div class="column col-6 form-group">
              <strong class="mr-2">Nome:</strong> {{ form.name }}
            </div>
            <div class="column col-6 form-group">
              <strong class="mr-2">Data nascimento:</strong> {{ form.birthDate | date }}
            </div>
            <div class="column col-6">
              <strong class="mr-2">
                CPF:
              </strong> {{ form.identity ? form.identity.value : '' | cpf }}
            </div>
            <div class="column col-6">
              <strong class="mr-2">Email:</strong> {{ form.email }}
            </div>
          </div>
        </div>

        <div class="columns">
          <div class="column col-12 form-group">
            <label class="form-checkbox">
              <input type="checkbox" v-model="changePassword">
              <i class="form-icon"/> Redefinir senha
            </label>
            <template v-if="changePassword">
              <div class="columns">
                <div class="column col-3 col-sm-12 form-group"
                     :class="{'has-error': $v.form.password.$error}">
                  <label for="password" class="form-label">Nova senha</label>
                  <input type="password" class="form-input" id="password"
                         v-model="form.password"
                         @change="$v.form.password.$touch()"
                            placeholder="senha">
                  <template v-if="$v.form.password.$error">
                    <p class="form-input-hint"
                       v-if="!$v.form.password.required">Campo obrigatório</p>
                    <p class="form-input-hint"
                       v-if="!$v.form.password.minLength">
                      A senha deve conter no mínimo 6 caracteres
                    </p>
                  </template>
                </div>
                <div class="column col-3 col-sm-12 form-group"
                     :class="{'has-error': $v.form.passwordConfirmation.$error}">
                  <label for="password-confirmation" class="form-label">
                    Confirme a nova senha
                  </label>
                  <input type="password" class="form-input" id="password-confirmation"
                         v-model="form.passwordConfirmation"
                         @change="$v.form.passwordConfirmation.$touch()"
                         placeholder="senha">
                  <template v-if="$v.form.passwordConfirmation.$error">
                    <p class="form-input-hint"
                       v-if="!$v.form.passwordConfirmation.required">
                      Campo obrigatório
                    </p>
                    <p class="form-input-hint"
                       v-else-if="!$v.form.passwordConfirmation.sameAsPass">
                      Senhas não conferem
                    </p>
                  </template>
                </div>
              </div>
            </template>
          </div>
        </div>

        <div class="divider" />
        <label class="form-label">Integrações</label>
        <div class="me-group">
          <div class="me-integration">
            <div>
              <strong>
                <fa-icon :icon="['fab', 'google']" class="mr-1" /> Google Calendar / Meet
              </strong>
              <div class="text-small text-gray" v-if="google.connected">
                Conectado como {{ google.email }}
              </div>
              <div class="text-small text-gray" v-else>
                Conecte sua conta Google para usar a telemedicina no prontuário.
              </div>
            </div>
            <button
              v-if="!google.connected"
              type="button"
              class="btn btn-primary btn-icon btn-icon-left"
              :class="{ loading: google.connecting }"
              :disabled="google.connecting"
              @click="connectGoogle"
            ><fa-icon :icon="['fab', 'google']" /> Conectar Google</button>
            <button
              v-else
              type="button"
              class="btn btn-error btn-icon btn-icon-left"
              :class="{ loading: google.disconnecting }"
              :disabled="google.disconnecting"
              @click="disconnectGoogle"
            ><fa-icon :icon="['fal', 'unlink']" /> Desconectar</button>
          </div>
          <template v-if="google.connected">
            <div class="divider" />
            <label class="form-switch">
              <input
                type="checkbox"
                v-model="google.calendarSync.enabled"
                @change="onToggleCalendarSync"
              >
              <i class="form-icon" /> Sincronizar minha agenda de atendimentos
              com o Google Calendar
            </label>
            <template v-if="google.calendarSync.enabled">
              <div class="loading mt-2" v-if="google.loadingCalendars" />
              <select
                v-else
                class="form-select mt-2"
                v-model="google.calendarSync.calendarId"
                @change="onSelectCalendar"
              >
                <option value="" disabled>Selecione uma agenda</option>
                <option v-for="cal in google.calendars" :key="cal.id" :value="cal.id">
                  {{ cal.summary }}{{ cal.primary ? ' (principal)' : '' }}
                </option>
              </select>
            </template>
          </template>
        </div>
      </div>
      <div class="card-footer">
        <button class="btn btn-primary btn-icon btn-icon-left mr-1" @click="save()"
                :disabled="saving || !changePassword" :class="{loading: saving}">
          <fa-icon :icon="['fal', 'save']"/>
          Salvar
        </button>
        <router-link to="/" class="btn">Voltar</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { required, minLength, sameAs } from 'vuelidate/src/validators';
import { mapState } from 'vuex';

export default {
  data() {
    return {
      path: '/me',
      loading: false,
      saving: false,
      changePassword: false,
      form: this.blankForm(),
      google: {
        connected: false,
        email: null,
        connecting: false,
        disconnecting: false,
        calendarSync: {
          enabled: false,
          calendarId: '',
          calendarName: '',
        },
        calendars: [],
        loadingCalendars: false,
        savingSync: false,
      },
    };
  },
  validations() {
    const rules = {
      form: {
        password: {},
        passwordConfirmation: {},
      },
    };

    if (this.changePassword) {
      rules.form.password = {
        required,
        minLength: minLength(6),
      };
      rules.form.passwordConfirmation = {
        required,
        sameAsPass: sameAs('password'),
      };
    }

    return rules;
  },
  watch: {
    changePassword: {
      handler() {
        this.clearPassword();
      },
    },
  },
  async mounted() {
    this.setData();
    this.loadGoogleStatus();
    this.checkGoogleRedirect();
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
  },
  methods: {
    blankForm() {
      return {
        form: {
          id: '',
          name: '',
          birthDate: '',
          gender: '',
          email: '',
          identity: {
            type: '',
            value: '',
          },
          imageUrl: '',
          password: '',
          passwordConfirmation: '',
        },
      };
    },
    setData() {
      this.form = {
        id: this.user.id,
        name: this.user.name,
        birthDate: this.user.birthDate,
        gender: this.user.gender,
        email: this.user.email,
        identity: this.user.identity,
        imageUrl: this.user.imageUrl,
        password: '',
        passwordConfirmation: '',
      };
    },
    clearPassword() {
      this.$v.form.$reset();
      this.form.password = '';
      this.form.passwordConfirmation = '';
    },
    save() {
      this.$v.form.$touch();
      if (this.$v.form.$error) {
        return null;
      }

      this.saving = true;

      const data = {};

      if (this.form.password) {
        data.password = this.form.password;
      }

      return this.$http.post('/me', data)
        .then(() => {
          this.$toast.show('Salvo com sucesso!');
        })
        .catch(() => {})
        .finally(() => {
          this.saving = false;
        });
    },
    loadGoogleStatus() {
      return this.$httpX.get('/google/oauth/status')
        .then(({ data }) => {
          this.google.connected = data.connected;
          this.google.email = data.email;
          this.google.calendarSync = {
            enabled: data.calendarSync?.enabled || false,
            calendarId: data.calendarSync?.calendarId || '',
            calendarName: data.calendarSync?.calendarName || '',
          };
          if (this.google.calendarSync.enabled) {
            this.loadCalendars();
          }
        })
        .catch(() => {});
    },
    loadCalendars() {
      this.google.loadingCalendars = true;
      return this.$httpX.get('/google/oauth/calendars')
        .then(({ data }) => {
          this.google.calendars = data;
        })
        .catch(this.$toast.error)
        .finally(() => {
          this.google.loadingCalendars = false;
        });
    },
    onToggleCalendarSync() {
      if (this.google.calendarSync.enabled) {
        this.loadCalendars();
        if (this.google.calendarSync.calendarId) {
          this.saveCalendarSync();
        }
      } else {
        this.saveCalendarSync();
      }
    },
    onSelectCalendar() {
      const selected = this.google.calendars
        .find(cal => cal.id === this.google.calendarSync.calendarId);
      this.google.calendarSync.calendarName = selected ? selected.summary : null;
      this.saveCalendarSync();
    },
    saveCalendarSync() {
      this.google.savingSync = true;
      return this.$httpX.put('/google/oauth/calendar-sync', {
        enabled: this.google.calendarSync.enabled,
        calendarId: this.google.calendarSync.calendarId,
        calendarName: this.google.calendarSync.calendarName,
      })
        .then(() => {
          this.$toast.success('Preferência de sincronização salva!');
        })
        .catch(this.$toast.error)
        .finally(() => {
          this.google.savingSync = false;
        });
    },
    checkGoogleRedirect() {
      const { google: googleStatus } = this.$route.query;

      if (googleStatus === 'connected') {
        this.$toast.success('Conta Google conectada com sucesso!');
      } else if (googleStatus === 'error') {
        this.$toast.error('Não foi possível conectar a conta Google. Tente novamente.');
      }

      if (googleStatus) {
        this.$router.replace({ path: '/me' });
      }
    },
    connectGoogle() {
      this.google.connecting = true;
      this.$httpX.get('/google/oauth/url')
        .then(({ data }) => {
          window.location.href = data.url;
        })
        .catch(this.$toast.error)
        .finally(() => {
          this.google.connecting = false;
        });
    },
    disconnectGoogle() {
      this.google.disconnecting = true;
      this.$httpX.delete('/google/oauth')
        .then(() => {
          this.google.connected = false;
          this.google.email = null;
          this.google.calendarSync = { enabled: false, calendarId: '', calendarName: '' };
          this.google.calendars = [];
        })
        .catch(this.$toast.error)
        .finally(() => {
          this.google.disconnecting = false;
        });
    },
  },
};
</script>

<style lang="scss">
  @import "./src/assets/scss/_variables.scss";
  .me-page {
    max-width: 1024px;
    .me-group {
      border: $border-width solid $border-color;
      border-radius: $border-radius;
      margin: $layout-spacing 0;
      padding: $layout-spacing;
    }
    .me-integration {
      align-items: center;
      display: flex;
      justify-content: space-between;
    }
  }
</style>
